import React from 'react';
import type { CommercialDashboardResponse } from '@troia-v3/types';

import { CustomizeMenu, MetricTile, SectionLabel } from '@/shared/components/ui';

import {
  COMMERCIAL_WIDGET_IDS,
  COMMERCIAL_WIDGETS,
  type CommercialWidgetId,
} from '../config/widgets';

interface WidgetsSectionProps {
  title: string;
  data: CommercialDashboardResponse;
  selected: CommercialWidgetId[];
  onChange: (ids: CommercialWidgetId[]) => void;
  defaults: CommercialWidgetId[];
  testId: string;
}

const MENU_ITEMS = COMMERCIAL_WIDGET_IDS.map((id) => ({
  id,
  label: COMMERCIAL_WIDGETS[id].label,
  group: COMMERCIAL_WIDGETS[id].group,
}));

/**
 * Bloco de indicadores personalizável. Qualquer widget do catálogo pode ir em
 * qualquer bloco; a escolha é salva no navegador (ver
 * `useCommercialDashboardPreferences`).
 *
 * Bloco esvaziado continua com o título e o botão — senão não haveria como
 * trazer os widgets de volta.
 */
export const WidgetsSection: React.FC<WidgetsSectionProps> = ({
  title,
  data,
  selected,
  onChange,
  defaults,
  testId,
}) => {
  const tiles = selected
    .map((id) => ({ id, content: COMMERCIAL_WIDGETS[id].build(data) }))
    .filter((t) => t.content !== null);

  return (
    <section className="flex flex-col gap-2.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 min-h-7">
        <SectionLabel>{title}</SectionLabel>
        <CustomizeMenu
          items={MENU_ITEMS}
          selected={selected}
          onChange={onChange}
          defaults={defaults}
          title="Widgets visíveis"
          data-testid={`${testId}-customize`}
        />
      </div>
      {tiles.length > 0 ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {tiles.map(({ id, content }) => {
            const def = COMMERCIAL_WIDGETS[id];
            if (!content) return null;
            return (
              <MetricTile
                key={id}
                icon={def.icon}
                label={def.label}
                value={content.value}
                deltaPct={content.deltaPct}
                {...(content.trend ? { trend: content.trend } : {})}
                caption={content.caption}
                {...(content.extra ? { extra: content.extra } : {})}
                testId={`widget-${id}`}
              />
            );
          })}
        </div>
      ) : (
        <div
          className="rounded-xl px-4 py-5 text-center text-xs border border-dashed"
          style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
          data-testid={`${testId}-empty`}
        >
          Nenhum widget neste bloco. Use “Personalizar” para escolher.
        </div>
      )}
    </section>
  );
};

WidgetsSection.displayName = 'WidgetsSection';
